const VERTEX_SHADER = `
    attribute vec2 aPosition;
    varying vec2 vUv;
    void main() {
        vUv = (aPosition + 1.0) * 0.5;
        gl_Position = vec4(aPosition, 0.0, 1.0);
    }`;

const STARFIELD_FRAGMENT_SHADER = `
    #ifdef GL_ES
    precision highp float;
    #endif

    #define PI 3.14159265

    uniform vec2 resolution;
    uniform float time;
    uniform sampler2D uAudioTex;
    uniform vec3 uColors[3];
    varying vec2 vUv;

    float vDrop(vec2 uv, float t, float audio)
    {
        uv.x = uv.x*52.0;						// H-Count
        float dx = fract(uv.x);
        uv.x = floor(uv.x);
        uv.y *= 0.050;							// stretch
        float o=sin(uv.x*315.4);				// offset
        float s=cos(uv.x*93.1)*.3 +.7;			// speed
        float trail = mix(95.0,5.0*(audio*0.5+0.5),s);			// trail length
        float yv = fract(uv.y + t*s + o) * trail;
        yv = 1.0/yv;
        yv = smoothstep(0.0,1.0,yv*yv);
        yv = sin(yv*PI)*(s*8.0);
        float d2 = sin(dx*PI);
        return yv*(d2*d2);
    }

    void main()
    {
        // Sample audio texture along x-axis
        //float audio = 1.0 - texture2D(uAudioTex, vec2(vUv.x, 0.0)).r;
        // Sample audio based on radius
        vec2 centered = vUv - 0.5;
        float r = length(centered);
        float audio = texture2D(uAudioTex, vec2(clamp(r * 1.4, 0.0, 1.0), 0.0)).r;
        float raudio = 1.0 - audio;

        vec2 p = (gl_FragCoord.xy - 0.5 * resolution.xy) / resolution.y;
        float d = length(p)+0.1;
        p = vec2(atan(p.x*(audio*0.5+0.5), p.y*(raudio*0.5+0.5)) / PI, 2.5 / d);
        float t =  mod(time, PI*2000.0) * 0.4;   // avoid precision issues

        vec3 col = uColors[0] * vDrop(p,t+(audio*0.3),raudio)*(raudio*0.5+0.5);
        col += uColors[1] * vDrop(p,t+(audio*0.3+0.33),raudio)*(raudio*0.5+0.5);
        col += uColors[2] * vDrop(p,t+(audio*0.3+0.66),raudio)*(raudio*0.5+0.5);
    
        col += vec3(5.05*audio,5.00*audio,4.95*audio) * vDrop(p,t+audio,raudio)*(raudio*0.5+0.5);	// white

        // Correct for aspect ratio so the circle stays round
        // Centered coords (-1 to +1)
        vec2 uv = gl_FragCoord.xy / resolution.xy;
        vec2 pc = uv * 2.0 - 1.0;
        pc.x *= resolution.x / resolution.y;

        float dd = length(pc);     // distance from center
        float radius = 1.0;        // circle radius
        float edge = 0.1;          // thickness of the fade

        // Smooth mask: 1.0 inside, 0.0 outside, smooth in between
        float mask = smoothstep(radius, radius - edge, dd);

        gl_FragColor = vec4(col*(d*d)*mask, 0.25*mask);
    }`;

const TUNNEL_FRAGMENT_SHADER = `
    #ifdef GL_ES
    precision highp float;
    #extension GL_OES_standard_derivatives : enable
    #endif

    #define PI 3.14159265

    uniform vec2 resolution;
    uniform float time;
    uniform sampler2D uAudioTex;
    uniform vec3 uColors[3];
    varying vec2 vUv;

    void main()
    {
        // Centred, aspect-corrected pixel coords
        vec2 p = (gl_FragCoord.xy - 0.5 * resolution.xy) / resolution.y;
        float dist = length(p);
        float angle = atan(p.y, p.x);                 // -PI to PI

        // Audio: sample by radial dist (bass near center, treble near edge)
        float audioR = texture2D(uAudioTex, vec2(clamp(dist, 0.0, 1.0), 0.0)).r;
        float audio  = audioR;

        // Classic tunnel projection:
        //   tunU = normalised angle       → horizontal tile coord
        //   tunV = depth (1 / dist)       → tile depth / forward scroll
        float tunU = angle / (2.0 * PI);
        float tunV = 0.3 / max(dist, 0.001);

        float phase = mod(time, 2000.0 * PI);

        // Fly into the tunnel; audio amplifies speed for beat kicks
        tunV += phase * 0.35 + audio * 0.5;

        // Gradual twist that deepens with travel and swells on loud hits
        tunU += tunV * 0.06 + audio * 0.12;

        // Sample audio by angle AFTER twist - fract(tunU+0.5) wraps seam-free
        // (raw atan wraps hard at ±PI on the left axis, causing a visible line)
        float audioA = texture2D(uAudioTex, vec2(fract(tunU + 0.5), 0.0)).r;

        // Tile the tunnel surface
        vec2 cell = vec2(tunU * 8.0, tunV * 5.0);

        // Anti-aliased tile/grid edges using screen-space derivatives
        vec2 cellFract = fract(cell);
        vec2 aa = fwidth(cell) * 0.75;
        vec2 edge = smoothstep(aa, aa * 2.0, cellFract) * smoothstep(vec2(1.0) - aa, vec2(1.0) - aa * 2.0, cellFract);
        float tileFace = edge.x * edge.y * (1.0 - min(cellFract.x, cellFract.y));   // 1.0 on tile faces, 0.0 on grid lines
        float gridLine = min(edge.x, edge.y);

        // Map each tile's depth-index to a frequency band for per-tile audio reactivity
        float band      = fract(abs(fract(tunV)));
        float tileAudio = texture2D(uAudioTex, vec2(band, 0.0)).r;

        // Start from the chosen palette, then dynamically bend each RGB channel with audio
        float pulse0 = 0.5 + 0.5 * sin(phase * 1.7 + tunV * 0.20 + audioA * 5.0);
        float pulse1 = 0.5 + 0.5 * sin(phase * 2.1 + tunV * 0.14 + tileAudio * 6.0 + 1.7);
        float pulse2 = 0.5 + 0.5 * sin(phase * 1.4 + tunU * 8.0  + audio * 4.0 + 3.2);

        // Colour: three palette colours modulating the tile faces
        vec3 col  = vec3(uColors[0].r * pulse0, uColors[0].g, uColors[0].b) * tileFace * (tileAudio * 1.2 + 0.10);
             col += vec3(uColors[1].r, uColors[1].g * pulse1, uColors[1].b) * tileFace * (audioA   * 1.0 + 0.05) * 0.3;
             col += vec3(uColors[2].r, uColors[2].g, uColors[2].b * pulse2) * tileFace * (audio    * 0.8 + 0.08) * 0.1;

        // Grid lines: dim ambient glow, flare on beats
        col += (uColors[0] * 0.4 + uColors[1] * 0.3) * gridLine * audio * 0.9;

        // White beat flash across tile faces
        col += vec3(audio * audio * 2.0) * tileFace * tileAudio;

        // Perspective depth brightness: tiles appear brighter as they recede
        float depthBright = clamp(0.25 / (dist + 0.05), 0.0, 1.0);
        col *= depthBright;

        // Correct for aspect ratio so the circle stays round
        // Centered coords (-1 to +1)
        vec2 uv = gl_FragCoord.xy / resolution.xy;
        vec2 pc = uv * 2.0 - 1.0;
        pc.x *= resolution.x / resolution.y;

        float dd = length(pc);     // distance from center
        float radius = 1.0;        // circle radius
        float maskEdge = 0.2;      // thickness of the fade

        // Smooth mask: 1.0 inside, 0.0 outside, smooth in between
        float mask = smoothstep(radius, radius - maskEdge, dd);

        // Soft centre mask: attenuate the intense vanishing-point area
        float centerMask = smoothstep(0.0, 0.1, dist);

        gl_FragColor = vec4(col * mask * centerMask, 0.85 * mask * centerMask);
    }`;

const SPHERE_FRAGMENT_SHADER = `
    #ifdef GL_ES
    precision highp float;
    #endif

    #define PI 3.14159265

    uniform vec2 resolution;
    uniform float time;
    uniform sampler2D uAudioTex;
    uniform vec3 uColors[3];
    varying vec2 vUv;

    float sphereSDF(vec3 p, float r) {
        return length(p) - r;
    }

    float rayMarch(vec3 ro, vec3 rd, float maxd) {
        float d = 0.0;
        for (int i = 0; i < 50; i++) {
            vec3 p = ro + rd * d;
            float dist = sphereSDF(p, 1.0);
            if (dist < 0.001) return d;
            d += dist;
            if (d > maxd) return -1.0;
        }
        return -1.0;
    }

    void main() {
        vec2 uv = (gl_FragCoord.xy - 0.5 * resolution.xy) / resolution.y;
        vec3 ro = vec3(0.0, -2.0, 0.0);
        vec3 rd = normalize(vec3(uv.x, 1.0, uv.y));

        float d = rayMarch(ro, rd, 10.0);
        vec3 col = vec3(0.0);

        if (d > 0.0) {
            vec3 p = ro + rd * d;
            float lenP = length(p);
            float theta = acos(p.z / lenP); // polar angle
            float phi = atan(p.y, p.x); // azimuthal angle

            float phase = mod(time, 2000.0 * PI);

            // Sample audio based on azimuthal angle and radial distance
            float audioPhi = texture2D(uAudioTex, vec2(fract(phi / (2.0 * PI) + 0.5), 0.0)).r;
            float audioRad = texture2D(uAudioTex, vec2(clamp(lenP * 0.5, 0.0, 1.0), 0.0)).r;
            float audio = (audioPhi + audioRad) * 0.5;

            // Modulate the strip position for dynamic, lightning-like effects with increased twist during loud sections
            float twistAmp = audio * 0.8; // Higher amplitude for louder audio
            float modulatedTheta = theta 
                + sin(phi * 3.0 + phase * 2.0) * twistAmp
                + sin(phi * 7.0 + phase * 3.0) * twistAmp * 0.6
                + cos(phi * 2.0 + phase * 1.5) * twistAmp * 0.4;
            float distToEquator = abs(modulatedTheta - PI / 2.0);

            // Variable strip width based on audio intensity, wider for loud parts
            float stripWidth = 0.25 + audio * 0.6;
            if (distToEquator < stripWidth) {
                float t = distToEquator / stripWidth; // 0 to 1
                // High-contrast color mixing with power for stronger effects, boosted during loud sections
                float intensityBoost = 1.0 + audio;
                col = mix(uColors[0] * 0.3, uColors[1] * 1.5, pow(audio, 1.5)) * (1.0 - t) * intensityBoost;
                // Add palette color 2 with contrast
                col += uColors[1] * pow(audio, 2.0) * 0.5 * (1.0 - t) * intensityBoost;
                // Intense white lightning flash during peaks
                col += vec3(pow(audio, 5.0) * 2.0) * (1.0 - t);

                // High-frequency audio wave layer with thin, glowing curves
                float highFreqBand = 0.75 + 0.2 * sin(phi * 5.0 + phase * 2.5);
                float audioHigh = texture2D(uAudioTex, vec2(clamp(highFreqBand, 0.0, 1.0), 0.0)).r;
                float wavePhase = phi * 20.0 + phase * 8.0 + audioHigh * 16.0;
                float wave = sin(wavePhase) * cos(theta * 4.0 + phase * .5);
                float waveThickness = 0.01 + audioHigh * 0.03;
                float waveMask = 1.0 - smoothstep(waveThickness, waveThickness + 0.008, abs(wave));
                waveMask *= pow(audioHigh, 2.2) * 2.5;
                waveMask *= (1.0 - distToEquator / stripWidth);
                vec3 waveColor = uColors[2] * 1.2;
                col += waveColor * waveMask;
                col += vec3(1.0, 0.9, 0.7) * waveMask * 0.6;
            }
        }

        // Circular mask
        vec2 pc = (gl_FragCoord.xy / resolution.xy) * 2.0 - 1.0;
        pc.x *= resolution.x / resolution.y;
        float dd = length(pc);
        float radius = 1.0;
        float maskEdge = 0.333;
        float mask = smoothstep(radius, radius - maskEdge, dd);

        gl_FragColor = vec4(col * mask, 0.0);
    }`;

export { VERTEX_SHADER, STARFIELD_FRAGMENT_SHADER, TUNNEL_FRAGMENT_SHADER, SPHERE_FRAGMENT_SHADER };